import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ConfirmationBox } from '../../shared';
import { addNewAccountHcPlatform, getAllHiringClient, popup } from '../../../utils';
import { EDIT_CUSTOMER as EDIT, HIRING_CLIENT, QUERY_KEYS, SOURCE } from '../../../constants';
import { AddHiringClient } from './AddHiringClient';
import useWindowSize from '../../../hooks/useWindowSize';
import { CircleSpinner, Delete, IconPlus } from '../../../assets/svg';
import SSCSelect from '../../shared/SSCSelect';
import { deleteKeysFromObject } from '../../../utils/common/helperFunctions';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

export const HiringClientSection = ({
    handleClose,
    id,
    tpa,
    realData,
    hiringClientData,
    setHiringClientData,
    deletedPlatformIds,
    setDeletedPlatformIds,
}) => {
    const queryClient = useQueryClient()
    const [addedHC, setAddedHC] = useState('');
    const [newHCs, setNewHCS] = useState({});
    const [hcsToDelete, setHcsToDelete] = useState({});
    const [showAddNewHC, setShowAddNewHC] = useState(false);
    const [searchHC, setSearchHC] = useState('');
    const [multiHCDeleteIds, setMultiHCDeleteIds] = useState([]);
    const [openConfirmationBox, setOpenConfirmationBox] = useState(false);
    const { width } = useWindowSize();

    const [debouncedHCSearch] = useDebouncedValue(searchHC, 500);
    const hiringClientQuery = useQuery({
        queryKey: [QUERY_KEYS.HIRING_CLIENTS, debouncedHCSearch],
        queryFn: async () =>
            await getAllHiringClient({ offset: 1, limit: 50, search: debouncedHCSearch }),
        select: (data) => {
            const ids = new Set(tpa?.hiringClients?.map((e) => e.id));
            return (
                data?.data
                    .filter((hc) => !ids.has(hc.id))
                    .map((ele) => ({ label: ele.name, value: ele })) || []
            );
        },
    });

    if (hiringClientQuery.isError) popup('error', hiringClientQuery.error.message);

    const updateGradeInHiringClientData = (e, selectedHiringClient, setHiringClientData) => {
        const { value } = e.target;

        setHiringClientData((prevData) =>
            prevData.map((pf) => {
                if (pf.id !== tpa.id) return pf;

                return {
                    ...pf,
                    hiringClients: pf.hiringClients.map((hc) =>
                        hc.id === selectedHiringClient.id
                            ? { ...hc, hiring_client_grade: value, updated: true }
                            : hc,
                    ),
                };
            }),
        );
    };

    // if new HC Added
    useEffect(() => {
        if (addedHC) {
            setSearchHC('');
            if (
                tpa?.hiringClients ||
                hiringClientData.filter((pf) => pf.id === tpa.id)[0]?.hiringClients
            ) {
                if (
                    hiringClientData
                        .filter((pf) => pf.id === tpa.id)[0]
                        ?.hiringClients?.some((hc) => hc.id === addedHC.id)
                ) {
                    popup('error', `${addedHC.name} already exist`);
                } else {
                    setHiringClientData((pre) =>
                        pre.map((pf) =>
                            pf.id === tpa.id
                                ? { ...pf, hiringClients: [...pf.hiringClients, addedHC] }
                                : pf,
                        ),
                    );
                    popup('info', `Added ${addedHC.name}`);
                }
            } else {
                setHiringClientData((pre) =>
                    pre.map((pf) => (pf.id === tpa.id ? { ...pf, hiringClients: [addedHC] } : pf)),
                );
                popup('info', `Added ${addedHC.name}`);
            }
        }
        setAddedHC('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addedHC, hiringClientData, tpa?.hiringClients, tpa?.id]);

    // handle delete click
    const handleDeleteHC = (ids) => {
        const idsToDelete = Array.isArray(ids) ? ids : [ids];
        const hcAfterDelete =
            hiringClientData
                ?.find((pf) => pf.id === tpa.id)
                ?.hiringClients.filter((hc) => !idsToDelete.includes(hc.id)) || [];

        setHiringClientData((prev) =>
            prev.map((pf) => (pf.id === tpa.id ? { ...pf, hiringClients: hcAfterDelete } : pf)),
        );
        const existingHcIds = hcsToDelete[tpa?.id] ? new Array(...hcsToDelete[tpa?.id]) : [];
        existingHcIds.push(...idsToDelete);
        setHcsToDelete((prev) => ({ ...prev, [tpa?.id]: [...new Set(existingHcIds)] }));
        setMultiHCDeleteIds([]); // Reset selection if doing bulk delete
    };

    // handle remove click
    const removeItem = (hc) => {
        const hcAfterRemove = hiringClientData
            .filter((pf) => pf.id === tpa.id)[0]
            .hiringClients.filter((HC) => HC.id !== hc.id);
        setHiringClientData((pre) =>
            pre.map((pf) => (pf.id === tpa.id ? { ...pf, hiringClients: hcAfterRemove } : pf)),
        );
    };

    // Select Hiring CLients
    const handleMultiHCSelect = (id) => {
        if (!multiHCDeleteIds.includes(id)) {
            setMultiHCDeleteIds((pre) => [...pre, id]);
        } else {
            setMultiHCDeleteIds((pre) => pre.filter((hc) => hc !== id));
        }
    };

    // Handle save button to open confirmation box
    const handleSaveHC = () => {
        if (realData !== hiringClientData) {
            setOpenConfirmationBox(true);
        } else {
            popup('error', EDIT.POPUP_MSG.NOTHING_TO_SAVE);
        }
    };

    const updateAHCPMutation = useMutation({
        mutationFn: async (body) =>
            await addNewAccountHcPlatform({
                body,
                account_id: id,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.ACCOUNT_BY_ID, id]
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.CUSTOMER_BY_ID, id]
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.CUSTOMERS],
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.SCORECARD_DETAILS],
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.HIRING_CLIENTS],
                exact: false,
            });
            setNewHCS({});
            setHcsToDelete({});
            setDeletedPlatformIds([]);
            popup('success', EDIT.POPUP_MSG.PLATFORM_UPDATED);
            handleClose();
        },
        onError: (err) => {
            popup('error', err.message);
        },
    });

    const onConfirmHandler = (v) => {
        if (v) {
            setMultiHCDeleteIds([]);
            let finalData = [];
            let updatedHCs = {};
            if (hiringClientData.length) {
                hiringClientData.forEach((pf) => {
                    const newHcIds = newHCs[pf.id]?.map((hc) => hc.id) || [];
                    if (pf.hiringClients?.length) {
                        pf.hiringClients.forEach((hc, index) => {
                            if (newHcIds.includes(hc.id)) {
                                finalData.push({
                                    account_id: id,
                                    hiring_client_id: hc.id,
                                    platform_id: pf.id,
                                    hc_order: index + 1,
                                    hiring_client_grade: hc.hiring_client_grade || '',
                                    platform_grade: pf.grade || null,
                                });
                            }
                        });
                    } else {
                        finalData.push({
                            account_id: id,
                            hiring_client_id: null,
                            platform_id: pf.id,
                            hc_order: 0,
                            hiring_client_grade: '',
                            platform_grade: null,
                        });
                    }
                });
                updatedHCs = hiringClientData.reduce((acc, item) => {
                    const hcIdsToExclude = finalData.map((hc) => hc.id);
                    const filtered = (item.hiringClients || [])
                        .filter((hc) => hc?.updated === true && !hcIdsToExclude.includes(hc.id))
                        .map(({ id, hiring_client_grade }) => ({
                            id,
                            hiring_client_grade,
                        }));

                    if (filtered.length > 0) {
                        acc[item.id] = filtered;
                    }

                    return acc;
                }, {});
            }
            if (
                !isEmpty(finalData) ||
                !isEmpty(hcsToDelete) ||
                !isEmpty(updatedHCs) ||
                !isEmpty(deletedPlatformIds)
            ) {
                const deletedHCs = deleteKeysFromObject(hcsToDelete, deletedPlatformIds);
                updateAHCPMutation.mutate({
                    newHCs: finalData,
                    deletedHCs,
                    updatedHCs,
                    deletedPlatformIds,
                });
            }
        }
    };

    const onHCSelection = (v) => {
        const hcs = newHCs[tpa?.id] ? new Array(...newHCs[tpa?.id]) : [];
        hcs.push(v);
        setNewHCS((prev) => ({ ...prev, [tpa?.id]: hcs }));
        setAddedHC(v);
    };

    return (
        <div
            className='border user-create-form mt-2 px-2 mb-5'
            style={{ marginInline: 0 }}
            key={tpa?.id}
        >
            <div
                className={`wrapper position-relative d-flex align-items-center gap-3 justify-content-between`}
                id='wrapper'
            >
                <div
                    className='d-flex flex-wrap align-items-center w-100 gap-3'
                    style={{ zIndex: 109 }}
                >
                    <div className=' d-flex justify-content-start align-items-center gap-3 w-100'>
                        <div className='d-flex justify-content-start align-items-center gap-4 w-100'>
                            <SSCSelect
                                placeholder={HIRING_CLIENT.SEARCH_BAR_PLACEHOLDER}
                                options={hiringClientQuery.data}
                                value={addedHC}
                                onChange={(v) => onHCSelection(v.value)}
                                onInputChange={setSearchHC}
                                className={width > 720 ? 'w-50' : 'w-75'}
                                isDisabled={isEmpty(tpa)}
                                isLoading={hiringClientQuery.isFetching}
                            />
                            {width > 720 ? (
                                <button
                                    onClick={() => setShowAddNewHC((pre) => !pre)}
                                    className='ssc-primary-green-btn button-green-filled'
                                    style={{ minWidth: 'max-content' }}
                                    disabled={isEmpty(tpa)}
                                >
                                    {EDIT.BUTTON_TEXT.ADD_NEW_HC}
                                </button>
                            ) : (
                                <button
                                    id='action-btn-green'
                                    title={EDIT.BUTTON_TEXT.ADD_NEW_HC}
                                    onClick={() => setShowAddNewHC((pre) => !pre)}
                                    disabled={isEmpty(tpa)}
                                >
                                    <IconPlus />
                                </button>
                            )}
                            {multiHCDeleteIds.length > 1 && (
                                <button
                                    title='Delete Selected Hiring Clients'
                                    onClick={() => handleDeleteHC(multiHCDeleteIds)}
                                    className='border-0 bg-white'
                                >
                                    <Delete width={22} height={22} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                {/* CLOSE BUTTON */}
                <div className='d-flex justify-content-end align-items-start'>
                    <button
                        type='button'
                        className='btn-close mt-0'
                        data-bs-dismiss='modal'
                        aria-label='Close'
                        onClick={() => {
                            setNewHCS({});
                            setHcsToDelete({});
                            setDeletedPlatformIds([]);
                            handleClose();
                        }}
                    />
                </div>
            </div>

            <div className='scroll-manage-platform mt-2 flex-grow-1 flex-fill draggable'>
                {showAddNewHC && (
                    <AddHiringClient setNewHC={setShowAddNewHC} setAddedHC={onHCSelection} />
                )}
                {!isEmpty(hiringClientData.filter((pf) => pf?.id === tpa?.id)[0]?.hiringClients) ? (
                    <table className='table mt-2'>
                        <thead style={{ top: 0, zIndex: 99 }}>
                            <tr>
                                <th className='edit-box-row'>Select</th>
                                <th className='text-nowrap sticky-th-left'>Hiring Client Name</th>
                                <th className='text-nowrap'>Created By</th>
                                <th>Grade</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {hiringClientData
                                .filter((pf) => pf.id === tpa.id)[0]
                                .hiringClients.map((hc) => (
                                    <tr key={hc.id}>
                                        <td className='edit-box-row'>
                                            {tpa?.hiringClients?.some((HC) => HC.id === hc.id) ? (
                                                <input
                                                    onChange={() => handleMultiHCSelect(hc.id)}
                                                    type='checkbox'
                                                    className='cursor-pointer'
                                                    checked={
                                                        multiHCDeleteIds.includes(hc.id)
                                                            ? true
                                                            : false
                                                    }
                                                />
                                            ) : (
                                                <button
                                                    onClick={() => removeItem(hc)}
                                                    className='btn-close'
                                                ></button>
                                            )}
                                        </td>
                                        <td className='sticky-td-left'>{hc.name}</td>
                                        <td>
                                            {hc.is_from_salesforce ? (
                                                <span className='text-danger'>
                                                    {SOURCE.SCHEDULAR}
                                                </span>
                                            ) : (
                                                SOURCE.CLOUD
                                            )}
                                        </td>
                                        <td>
                                            <div className='input-set w-100 input-set-gap'>
                                                <input
                                                    type='text'
                                                    name='grade-text'
                                                    onChange={(e) =>
                                                        updateGradeInHiringClientData(
                                                            e,
                                                            hc,
                                                            setHiringClientData,
                                                        )
                                                    }
                                                    placeholder={'Enter Grade'}
                                                    value={hc.hiring_client_grade || ''}
                                                    className={`form-control max-width-40-percent`}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                id='action-btn'
                                                title='Delete'
                                                onClick={() => handleDeleteHC(hc.id)}
                                                className='bg-white'
                                            >
                                                <Delete width={16} height={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                ) : (
                    <div className='text-special text-center ms-5 mt-3'>
                        {EDIT.BUTTON_TEXT.NO_HC}
                    </div>
                )}
            </div>
            <div className='d-flex align-items-center justify-content-end pt-lg-2 border-top'>
                <button
                    onClick={handleSaveHC}
                    className='ssc-primary-green-btn button-green-filled form-button d-flex align-items-center justify-content-center border-0'
                >
                    {updateAHCPMutation.isPending && (
                        <span className='me-1'>
                            <CircleSpinner />
                        </span>
                    )}
                    {EDIT.BUTTON_TEXT.SAVE}
                </button>
            </div>
            <ConfirmationBox
                setConfirm={(v) => onConfirmHandler(v)}
                openConfirmationBox={openConfirmationBox}
                setOpenConfirmationBox={setOpenConfirmationBox}
                customMessage={EDIT.POPUP_MSG.ARE_YOU_SURE}
            />
        </div>
    );
};

HiringClientSection.propTypes = {
    id: PropTypes.string,
    handleClose: PropTypes.func,
    realData: PropTypes.array,
    tpa: PropTypes.object,
    hiringClientData: PropTypes.array,
    setHiringClientData: PropTypes.func,
};
